import { useLingui } from '@lingui/react/macro';
import { RefreshCw } from 'lucide-react';
import { useId, type ReactNode } from 'react';

import type { ApiError } from '@/shared/http';
import type { Permission } from '@/shared/screen';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/ui/cn';

import type { BlockPresentation } from './block';
import { EmptyState, ErrorState, ForbiddenState, StaleNotice } from './states';
import { formatAgo, useNow } from './time';
import type { BlockState } from './use-block-data';

export interface BlockFrameProps {
  /** Título da seção, já traduzido. Nomeia a seção e o botão de atualizar. */
  readonly title: string;
  readonly state: BlockState;
  readonly presentation: BlockPresentation;
  readonly updatedAt?: number | undefined;
  readonly onRefresh?: (() => void) | undefined;
  /** Refazendo a busca (gira o ícone de atualizar e marca `aria-busy`). */
  readonly refreshing?: boolean;
  /** Página anterior na tela enquanto a nova carrega: esmaece em vez de piscar (U39 §8). */
  readonly dimmed?: boolean;
  readonly skeleton: ReactNode;
  /** Conteúdo do vazio; o padrão é um `EmptyState` genérico. */
  readonly empty?: ReactNode;
  readonly error?: ApiError | undefined;
  readonly missing?: readonly Permission[];
  /** Ações do cabeçalho do bloco ("Ver todos"). */
  readonly actions?: ReactNode;
  readonly headingLevel?: 2 | 3;
  /** Nome acessível da região quando o mesmo bloco aparece mais de uma vez (prévia, catálogo). */
  readonly label?: string | undefined;
  readonly className?: string;
  /** O conteúdo com dado (`ok` e `stale`). */
  readonly children?: ReactNode;
}

/**
 * Casca comum de todo bloco (09 §10.4): título, "atualizado há X", atualizar e os seis estados.
 * É um container (`@container/block`): P ≤ 360 px, M ≤ 720 px, G acima, pelas variantes
 * `@min-[361px]/block:` e `@min-[721px]/block:`.
 */
export function BlockFrame({
  title,
  state,
  presentation,
  updatedAt,
  onRefresh,
  refreshing = false,
  dimmed = false,
  skeleton,
  empty,
  error,
  missing,
  actions,
  headingLevel = 2,
  label,
  className,
  children,
}: BlockFrameProps) {
  const { t, i18n } = useLingui();
  const headingId = useId();
  const now = useNow();
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  const mobile = presentation === 'mobile';
  const hasData = state === 'ok' || state === 'stale';
  // `ok` e `stale` dividem a chave: o aviso entra sem refazer o fade do conteúdo (U38).
  const phase = hasData ? 'data' : state;
  const retry = onRefresh ? () => onRefresh() : undefined;

  const body =
    state === 'loading' ? (
      <>
        <span className="sr-only">{t`Carregando ${title}`}</span>
        {skeleton}
      </>
    ) : state === 'empty' ? (
      (empty ?? <EmptyState title={t`Nada por aqui ainda`} compact={mobile} />)
    ) : state === 'error' ? (
      <ErrorState error={error} onRetry={retry} retrying={refreshing} compact={mobile} />
    ) : state === 'forbidden' ? (
      <ForbiddenState missing={missing ?? []} compact={mobile} />
    ) : (
      <div className="flex flex-col gap-3">
        {state === 'stale' ? <StaleNotice updatedAt={updatedAt} now={now} onRetry={retry} retrying={refreshing} error={error} /> : null}
        <div className={cn('transition-opacity duration-(--lume-dur-fast)', dimmed ? 'opacity-55' : null)}>{children}</div>
      </div>
    );

  const ago = updatedAt === undefined ? '' : formatAgo(updatedAt, now, i18n.locale);
  const updated =
    hasData && updatedAt !== undefined ? (
      <time
        dateTime={new Date(updatedAt).toISOString()}
        title={new Intl.DateTimeFormat(i18n.locale, { dateStyle: 'short', timeStyle: 'medium' }).format(updatedAt)}
        className="truncate text-xs text-muted-foreground"
      >
        {t`Atualizado ${ago}`}
      </time>
    ) : null;

  return (
    <section
      aria-labelledby={label ? undefined : headingId}
      aria-label={label}
      aria-busy={state === 'loading' || refreshing || undefined}
      data-slot="block-frame"
      data-state={state}
      data-presentation={presentation}
      className={cn(
        '@container/block min-w-0',
        mobile ? 'flex flex-col gap-3' : 'rounded-card border border-border bg-card text-card-foreground shadow-1',
        className,
      )}
    >
      <header className={cn('flex min-h-10 items-center gap-2', mobile ? null : 'border-b border-border px-4 py-2')}>
        <div className="flex min-w-0 flex-1 flex-col @min-[361px]/block:flex-row @min-[361px]/block:items-baseline @min-[361px]/block:gap-2">
          <Heading id={headingId} className={cn('truncate font-semibold text-foreground', mobile ? 'text-base' : 'text-sm')}>
            {title}
          </Heading>
          {updated}
        </div>
        {actions}
        {onRefresh && state !== 'forbidden' ? (
          <Button
            variant="ghost"
            size="icon"
            className={mobile ? undefined : 'size-control-sm'}
            aria-label={t`Atualizar ${title}`}
            disabled={refreshing || state === 'loading'}
            onClick={onRefresh}
          >
            <RefreshCw aria-hidden className={refreshing ? 'animate-spin' : undefined} />
          </Button>
        ) : null}
      </header>
      <div key={phase} data-slot="block-body" className={cn('animate-fade-in', mobile ? null : 'p-4')}>
        {body}
      </div>
    </section>
  );
}
